import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  MenuItem,
  InputAdornment
} from '@mui/material';
import { SucursalDto } from '../../../../services/api/sucursalesApi';
import { useEmpresasApi } from '../../../../services/api/empresasApi';

interface SucursalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SucursalDto) => Promise<void>;
  title: string;
  initialData?: SucursalDto;
  isLoading: boolean;
  isEdit?: boolean;
}

const SucursalModal: React.FC<SucursalModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialData,
  isLoading,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState<SucursalDto>({
    IdEmpresa: 0,
    Nombre: '',
    Telefono: '',
    Direccion: '',
    Target: 0,
    Activo: true,
    Eliminado: false
  });

  const [empresas, setEmpresas] = useState<Array<{ Id: number; Nombre: string }>>([]);
  const { getAll: getAllEmpresas } = useEmpresasApi();

  useEffect(() => {
    const loadEmpresas = async () => {
      const empresasData = await getAllEmpresas();
      setEmpresas(empresasData.filter(empresa => empresa.Activo));
    };
    loadEmpresas();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        IdEmpresa: 0,
        Nombre: '',
        Telefono: '',
        Direccion: '',
        Target: 0,
        Activo: true,
        Eliminado: false
      });
    }
  }, [initialData, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="Empresa"
            fullWidth
            required
            value={formData.IdEmpresa}
            onChange={(e) => setFormData({ ...formData, IdEmpresa: Number(e.target.value) })}
            sx={{ mb: 2 }}
          >
            <MenuItem value={0} disabled>Seleccione una empresa</MenuItem>
            {empresas.map((empresa) => (
              <MenuItem key={empresa.Id} value={empresa.Id}>
                {empresa.Nombre}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre"
            fullWidth
            required
            value={formData.Nombre}
            onChange={(e) => setFormData({ ...formData, Nombre: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Teléfono"
            fullWidth
            required
            value={formData.Telefono}
            onChange={(e) => setFormData({ ...formData, Telefono: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Dirección"
            fullWidth
            required
            value={formData.Direccion}
            onChange={(e) => setFormData({ ...formData, Direccion: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Objetivo Diario $ COP"
            type="number"
            fullWidth
            required
            value={formData.Target}
            onChange={(e) => {
              const value = e.target.value;
              // Remover caracteres no numéricos excepto el punto
              const numericValue = value.replace(/[^\d.]/g, '');
              // Convertir a número y actualizar el estado
              setFormData({ ...formData, Target: Number(numericValue) || 0 });
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputProps: { 
                min: 0,
                step: 1, // Cambiado a 1 ya que en COP no se usan decimales
                style: { textAlign: 'right' } // Alinear números a la derecha
              }
            }}
            sx={{ mb: 2 }}
          />
          {!isEdit && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.Activo}
                  onChange={(e) => setFormData({ ...formData, Activo: e.target.checked })}
                />
              }
              label="Activo"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading || formData.IdEmpresa === 0}>
            {isLoading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SucursalModal;